import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { propertyAPI, getImageUrlOrLocalImage } from '../../lib/api';
import toast from 'react-hot-toast';

const AMENITIES = ['WiFi', 'Air Conditioning', 'Heating', 'Kitchen', 'Washer', 'Dryer', 'Parking', 'Pool', 'Hot Tub', 'Gym', 'BBQ', 'Fireplace', 'Beach Access', 'Mountain View', 'Lake View', 'TV', 'Coffee Maker', 'Dishwasher', 'Balcony', 'Garden'];

const PropertyFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const [form, setForm] = useState({
    title: '', description: '', type: 'house', status: 'draft',
    address: { street: '', city: '', state: '', country: '', zip: '' },
    bedrooms: 1, bathrooms: 1, maxGuests: 2, squareFeet: '',
    pricing: { basePrice: '', cleaningFee: 0, securityDeposit: 0, extraGuestFee: 0, extraGuestAfter: 2, minimumNights: 1, maximumNights: 30 },
    rules: { smokingAllowed: false, petsAllowed: false, partiesAllowed: false, checkInTime: '15:00', checkOutTime: '11:00', additionalRules: [] },
    amenities: [],
    availability: { instantBooking: false, advanceNotice: 1 },
    images: []
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      propertyAPI.getOne(id)
        .then(res => {
          const data = res.data.data;
          // Add id field to existing images for React key
          if (data.images) {
            data.images = data.images.map((img, idx) => ({
              ...img,
              id: img._id || img.id || `existing-${idx}`
            }));
          }
          setForm(data);
        })
        .catch(() => toast.error('Failed to load property'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (path, value) => {
    setForm(prev => {
      const parts = path.split('.');
      if (parts.length === 1) return { ...prev, [path]: value };
      if (parts.length === 2) return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } };
      return prev;
    });
  };

  const toggleAmenity = (a) => {
    setForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(a) ? prev.amenities.filter(x => x !== a) : [...prev.amenities, a]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // First create/update the property without images
      const { images, ...propertyData } = form;
      let propertyId;
      
      if (isEdit) {
        await propertyAPI.update(id, propertyData);
        propertyId = id;
        toast.success('Property updated');
      } else {
        const res = await propertyAPI.create(propertyData);
        propertyId = res.data.data._id;
        toast.success('Property created');
      }

      // Upload images separately if there are new files
      const newImageFiles = images.filter(img => img.file);
      if (newImageFiles.length > 0) {
        const formData = new FormData();
        newImageFiles.forEach(img => {
          formData.append('images', img.file);
        });
        await propertyAPI.uploadImages(propertyId, formData);
      }

      navigate(`/properties/${propertyId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  const tabs = ['basic', 'location', 'pricing', 'amenities', 'rules', 'images'];
  const currentTabIndex = tabs.indexOf(activeTab);

  const handleNext = () => {
    if (currentTabIndex < tabs.length - 1) {
      setActiveTab(tabs[currentTabIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (currentTabIndex > 0) {
      setActiveTab(tabs[currentTabIndex - 1]);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{isEdit ? 'Edit Property' : 'Add New Property'}</h1>
            <p className="page-subtitle">{isEdit ? 'Update property details' : 'Fill in the details to list your property'}</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <><div className="spinner" style={{ borderTopColor: '#fff' }} /> Saving...</> : <><Save size={16} /> {isEdit ? 'Update' : 'Create'} Property</>}
        </button>
      </div>

      <div className="tabs">
        {tabs.map(t => <button key={t} className={`tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>)}
      </div>

      <div className="card">
        {activeTab === 'basic' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Property Title *</label>
                <input className="form-input" placeholder="e.g. Lakeside Mountain Cabin" value={form.title} onChange={e => set('title', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
                  {['villa', 'cabin', 'apartment', 'house', 'condo', 'resort', 'hotel', 'other'].map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {['draft', 'active', 'inactive', 'maintenance'].map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows={5} placeholder="Describe your property..." value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
            <div className="form-grid form-grid-3">
              {[['bedrooms', 'Bedrooms'], ['bathrooms', 'Bathrooms'], ['maxGuests', 'Max Guests'], ['squareFeet', 'Square Feet']].map(([field, label]) => (
                <div className="form-group" key={field}>
                  <label className="form-label">{label}</label>
                  <input type="number" className="form-input" min={0} value={form[field] || ''} onChange={e => set(field, parseInt(e.target.value) || 0)} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Instant Booking</label>
                <select className="form-select" value={form.availability?.instantBooking ? 'true' : 'false'} onChange={e => set('availability.instantBooking', e.target.value === 'true')}>
                  <option value="false">Requires Approval</option>
                  <option value="true">Instant Book</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'location' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Street Address *</label>
              <input className="form-input" placeholder="123 Mountain View Rd" value={form.address?.street || ''} onChange={e => set('address.street', e.target.value)} />
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input className="form-input" placeholder="Aspen" value={form.address?.city || ''} onChange={e => set('address.city', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">State / Province</label>
                <input className="form-input" placeholder="Colorado" value={form.address?.state || ''} onChange={e => set('address.state', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Country *</label>
                <input className="form-input" placeholder="USA" value={form.address?.country || ''} onChange={e => set('address.country', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP / Postal Code</label>
                <input className="form-input" placeholder="81611" value={form.address?.zip || ''} onChange={e => set('address.zip', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="form-grid form-grid-2" style={{ gap: 20 }}>
            {[
              ['pricing.basePrice', 'Base Price / Night *', 'e.g. 150'],
              ['pricing.cleaningFee', 'Cleaning Fee', 'e.g. 75'],
              ['pricing.securityDeposit', 'Security Deposit', 'e.g. 300'],
              ['pricing.extraGuestFee', 'Extra Guest Fee / Night', 'e.g. 25'],
              ['pricing.extraGuestAfter', 'Extra Fee After N Guests', 'e.g. 2'],
              ['pricing.minimumNights', 'Minimum Nights', 'e.g. 2'],
              ['pricing.maximumNights', 'Maximum Nights', 'e.g. 30'],
            ].map(([field, label, placeholder]) => (
              <div className="form-group" key={field}>
                <label className="form-label">{label}</label>
                <input type="number" className="form-input" placeholder={placeholder} min={0}
                  value={field.includes('.') ? form[field.split('.')[0]]?.[field.split('.')[1]] || '' : form[field] || ''}
                  onChange={e => set(field, parseFloat(e.target.value) || 0)} />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'amenities' && (
          <div>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>Select all available amenities</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {AMENITIES.map(a => (
                <button key={a} type="button"
                  onClick={() => toggleAmenity(a)}
                  className={`btn btn-sm ${form.amenities?.includes(a) ? 'btn-primary' : 'btn-secondary'}`}>
                  {form.amenities?.includes(a) ? '✓ ' : ''}{a}
                </button>
              ))}
            </div>
            <p style={{ marginTop: 16, fontSize: 13, color: '#9CA3AF' }}>
              {form.amenities?.length || 0} amenities selected
            </p>
          </div>
        )}

        {activeTab === 'rules' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="form-grid form-grid-2">
              {[
                ['rules.smokingAllowed', 'Smoking'],
                ['rules.petsAllowed', 'Pets'],
                ['rules.partiesAllowed', 'Parties / Events'],
              ].map(([field, label]) => (
                <div className="form-group" key={field}>
                  <label className="form-label">{label} Allowed</label>
                  <select className="form-select"
                    value={form.rules?.[field.split('.')[1]] ? 'true' : 'false'}
                    onChange={e => set(field, e.target.value === 'true')}>
                    <option value="false">Not Allowed</option>
                    <option value="true">Allowed</option>
                  </select>
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Check-in Time</label>
                <input type="time" className="form-input" value={form.rules?.checkInTime || '15:00'} onChange={e => set('rules.checkInTime', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Check-out Time</label>
                <input type="time" className="form-input" value={form.rules?.checkOutTime || '11:00'} onChange={e => set('rules.checkOutTime', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'images' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Property Images</h3>
              <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>
                Add high-quality photos of your property. The first image will be the cover photo.
              </p>
              
              {/* Image Upload Area */}
              <div style={{
                border: '2px dashed #D1D5DB',
                borderRadius: 12,
                padding: '40px 20px',
                textAlign: 'center',
                background: '#F9FAFB',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => document.getElementById('image-upload').click()}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#3B82F6';
                e.target.style.background = '#EFF6FF';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.background = '#F9FAFB';
              }}>
                <Plus size={40} color="#9CA3AF" style={{ margin: '0 auto 12px' }} />
                <p style={{ fontSize: 16, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                  Click to upload images
                </p>
                <p style={{ fontSize: 13, color: '#9CA3AF' }}>
                  PNG, JPG, GIF up to 10MB each
                </p>
                <input
                  id="image-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const newImages = files.map((file, index) => ({
                      id: Date.now() + index,
                      url: URL.createObjectURL(file),
                      file,
                      isCover: form.images.length === 0 && index === 0,
                      caption: ''
                    }));
                    setForm(prev => ({
                      ...prev,
                      images: [...prev.images, ...newImages]
                    }));
                    e.target.value = '';
                  }}
                />
              </div>
            </div>

            {/* Image Gallery */}
            {form.images.length > 0 && (
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                  Uploaded Images ({form.images.length})
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                  {form.images.map((image, index) => (
                    <div key={image.id} className="card" style={{ padding: 12 }}>
                      <div style={{ position: 'relative', marginBottom: 8 }}>
                        <img
                          src={getImageUrlOrLocalImage(image.url)}
                          alt={`Property image ${index + 1}`}
                          style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }}
                        />
                        <button
                          className="btn btn-danger btn-icon btn-sm"
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onClick={async () => {
                            // If it's an existing image (has _id), delete from backend
                            if (image._id && isEdit) {
                              try {
                                await propertyAPI.deleteImage(id, image._id);
                                setForm(prev => ({
                                  ...prev,
                                  images: prev.images.filter(img => img.id !== image.id)
                                }));
                              } catch (err) {
                                toast.error('Failed to delete image');
                              }
                            } else {
                              // Just remove from local state for new images
                              setForm(prev => ({
                                ...prev,
                                images: prev.images.filter(img => img.id !== image.id)
                              }));
                            }
                          }}>
                          <Trash2 size={14} />
                        </button>
                        {image.isCover && (
                          <div style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: '#3B82F6',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            Cover
                          </div>
                        )}
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Image caption (optional)"
                          value={image.caption || ''}
                          onChange={(e) => {
                            const updatedImages = [...form.images];
                            updatedImages[index] = { ...image, caption: e.target.value };
                            setForm(prev => ({ ...prev, images: updatedImages }));
                          }}
                        />
                      </div>
                      {!image.isCover && (
                        <button
                          className="btn btn-sm btn-secondary"
                          style={{ width: '100%', marginTop: 8 }}
                          onClick={async () => {
                            // If it's an existing image (has _id), set cover via API
                            if (image._id && isEdit) {
                              try {
                                await propertyAPI.setCoverImage(id, image._id);
                                const updatedImages = form.images.map(img => ({
                                  ...img,
                                  isCover: img._id === image._id
                                }));
                                setForm(prev => ({ ...prev, images: updatedImages }));
                                toast.success('Cover image updated');
                              } catch (err) {
                                toast.error('Failed to set cover image');
                              }
                            } else {
                              // Just update local state for new images
                              const updatedImages = form.images.map(img => ({
                                ...img,
                                isCover: img.id === image.id
                              }));
                              setForm(prev => ({ ...prev, images: updatedImages }));
                            }
                          }}>
                          Set as Cover
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, padding: '0 20px' }}>
        <button 
          className="btn btn-secondary" 
          onClick={handlePrevious} 
          disabled={currentTabIndex === 0}
        >
          Previous
        </button>
        
        <div style={{ display: 'flex', gap: 8 }}>
          {tabs.map((_, index) => (
            <div 
              key={index}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: index === currentTabIndex ? '#3B82F6' : '#D1D5DB',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab(tabs[index])}
            />
          ))}
        </div>

        {currentTabIndex === tabs.length - 1 ? (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><div className="spinner" style={{ borderTopColor: '#fff' }} /> Saving...</> : <><Save size={16} /> {isEdit ? 'Update' : 'Create'} Property</>}
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleNext}>
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default PropertyFormPage;
